import { useEffect } from "react";
import { useNavigate, useParams, Routes, Route, Navigate } from "react-router";
import { Sidebar } from "@/widgets/project-sidebar";
import { ProjectView } from "./ProjectView";
import { AllProjectsView } from "./AllProjectsView";
import { EmptyState } from "./EmptyState";
import { WelcomeState } from "./WelcomeState";
import { useJobs, useMe } from "@/lib/api";
import { NewProjectPage } from "@/screens/NewProjectPage";

type View = "home" | "all" | "processing" | "project" | "new";

function MainContent({ view }: { view: View }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: jobs } = useJobs();
  const { data: me } = useMe();

  const newFlowEnabled = me?.new_flow_enabled ?? false;
  const position = id ? Number(id) : null;

  const handleSelectJob = (pos: number) => {
    if (pos === -1) navigate("/all");
    else if (pos === -2) navigate("/processing");
    else navigate(`/reports/${pos}`);
  };

  // Unified flow: every "New project" action opens the single New Project page.
  const handleNewProject = () => navigate("/new");

  // Legacy flow only: auto-open the first completed report at root. New-flow users
  // land on the welcome state instead, so the home view stays put.
  useEffect(() => {
    if (view === "home" && !newFlowEnabled && jobs && jobs.length > 0) {
      const firstCompleted = jobs.find((j) => j.status === "COMPLETED");
      if (firstCompleted) {
        navigate(`/reports/${firstCompleted.position}`, { replace: true });
      }
    }
  }, [view, jobs, navigate, newFlowEnabled]);

  // Sidebar selection marker: -1 = all, -2 = processing, else position number, null = none
  const sidebarSelected =
    view === "all" ? -1 :
    view === "processing" ? -2 :
    view === "project" && position !== null ? position :
    null;

  const renderHome = () =>
    newFlowEnabled ? (
      <WelcomeState onStart={() => navigate("/new")} />
    ) : (
      <EmptyState onUpload={handleNewProject} />
    );

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        selectedJobPosition={sidebarSelected}
        onSelectJob={handleSelectJob}
        onNewProject={handleNewProject}
      />

      <main className="flex-1 min-w-0 overflow-hidden">
        {view === "new" ? (
          <NewProjectPage />
        ) : view === "all" ? (
          <AllProjectsView onSelectJob={handleSelectJob} />
        ) : view === "processing" ? (
          <AllProjectsView onSelectJob={handleSelectJob} filterProcessing />
        ) : view === "project" && position !== null ? (
          <ProjectView position={position} />
        ) : (
          renderHome()
        )}
      </main>
    </div>
  );
}

/** Legacy /brief/:roleSlug → unified New Project page, preserving the role. */
function BriefRedirect() {
  const { roleSlug } = useParams();
  return <Navigate to={roleSlug ? `/new?role=${roleSlug}` : "/new"} replace />;
}

export function ProtectedLayout() {
  // One layout for everyone (sidebar + main area). New-flow users see a welcome
  // state at root; legacy users keep auto-open-first-report. The standalone
  // /catalog, /brief, and /dashboard pages now fold into the unified /new flow.
  return (
    <Routes>
      <Route path="/" element={<MainContent view="home" />} />
      <Route path="/new" element={<MainContent view="new" />} />
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      <Route path="/catalog" element={<Navigate to="/new" replace />} />
      <Route path="/brief/:roleSlug" element={<BriefRedirect />} />
      <Route path="/all" element={<MainContent view="all" />} />
      <Route path="/processing" element={<MainContent view="processing" />} />
      <Route path="/reports/:id" element={<MainContent view="project" />} />
    </Routes>
  );
}
